import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { FieldGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  CheckIcon,
  PencilIcon,
  PlusIcon,
  ScissorsIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
  UNCATEGORISED_SERVICE_CATEGORY,
  groupServicesByCategory,
} from '../../service-category-groups';

interface Step5ServicesProps {
  // biome-ignore lint/suspicious/noExplicitAny: Form type flexibility needed
  form: UseFormReturn<any>;
}

/**
 * Step 5: Services
 * Simple list for adding/removing services by name.
 * Service details can be configured later from the dashboard.
 */
export function Step5Services({ form }: Step5ServicesProps) {
  const [newServiceName, setNewServiceName] = useState('');
  const [editingServiceName, setEditingServiceName] = useState<string | null>(
    null
  );
  const [editedServiceName, setEditedServiceName] = useState('');

  const services: string[] = form.watch('services') || [];
  const serviceCategories: Record<string, string> =
    form.watch('serviceCategories') || {};
  const details: Record<string, { description?: string }> =
    form.watch('serviceDetails') || {};
  const groupedServices = groupServicesByCategory(
    services.map((name) => ({
      name,
      categoryName: serviceCategories[name],
    }))
  );

  const addServiceToForm = useCallback(
    (name: string) => {
      const currentServices: string[] = form.getValues('services') || [];
      if (currentServices.includes(name)) return;

      const currentDetails = form.getValues('serviceDetails') || {};
      form.setValue(
        'serviceDetails',
        {
          ...currentDetails,
          [name]: {
            appointmentDuration: 30,
            requiresDeposit: false,
            pricingDescription: '',
          },
        },
        { shouldDirty: true }
      );
      form.setValue('services', [...currentServices, name], {
        shouldDirty: true,
      });
      const currentCategories = form.getValues('serviceCategories') || {};
      form.setValue(
        'serviceCategories',
        {
          ...currentCategories,
          [name]: UNCATEGORISED_SERVICE_CATEGORY,
        },
        { shouldDirty: true }
      );
    },
    [form]
  );

  const removeServiceFromForm = useCallback(
    (name: string) => {
      const currentServices: string[] = form.getValues('services') || [];
      form.setValue(
        'services',
        currentServices.filter((s) => s !== name),
        { shouldDirty: true }
      );
      const currentDetails = form.getValues('serviceDetails') || {};
      const { [name]: _, ...rest } = currentDetails;
      form.setValue('serviceDetails', rest, { shouldDirty: true });
      const currentCategories = form.getValues('serviceCategories') || {};
      const { [name]: _removedCategory, ...remainingCategories } =
        currentCategories;
      form.setValue('serviceCategories', remainingCategories, {
        shouldDirty: true,
      });
      if (editingServiceName === name) {
        setEditingServiceName(null);
        setEditedServiceName('');
      }
    },
    [editingServiceName, form]
  );

  const startEditingService = useCallback((name: string) => {
    setEditingServiceName(name);
    setEditedServiceName(name);
  }, []);

  const cancelEditingService = useCallback(() => {
    setEditingServiceName(null);
    setEditedServiceName('');
  }, []);

  const saveEditedService = useCallback(() => {
    if (!editingServiceName) return;

    const trimmed = editedServiceName.trim();
    if (
      !trimmed ||
      (trimmed !== editingServiceName && services.includes(trimmed))
    ) {
      return;
    }

    if (trimmed !== editingServiceName) {
      form.setValue(
        'services',
        services.map((service) =>
          service === editingServiceName ? trimmed : service
        ),
        { shouldDirty: true }
      );

      const currentDetails = form.getValues('serviceDetails') || {};
      const { [editingServiceName]: renamedDetails, ...remainingDetails } =
        currentDetails;
      form.setValue(
        'serviceDetails',
        {
          ...remainingDetails,
          ...(renamedDetails ? { [trimmed]: renamedDetails } : {}),
        },
        { shouldDirty: true }
      );

      const currentCategories = form.getValues('serviceCategories') || {};
      const { [editingServiceName]: renamedCategory, ...remainingCategories } =
        currentCategories;
      form.setValue(
        'serviceCategories',
        {
          ...remainingCategories,
          ...(renamedCategory ? { [trimmed]: renamedCategory } : {}),
        },
        { shouldDirty: true }
      );
    }

    cancelEditingService();
  }, [
    cancelEditingService,
    editedServiceName,
    editingServiceName,
    form,
    services,
  ]);

  const addCustomService = useCallback(() => {
    const trimmed = newServiceName.trim();
    if (!trimmed || services.includes(trimmed)) return;

    addServiceToForm(trimmed);
    setNewServiceName('');
  }, [newServiceName, services, addServiceToForm]);

  return (
    <FieldGroup className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Your Services</h1>
        <p className="text-muted-foreground">
          {services.length > 0
            ? `We found ${services.length} services across ${groupedServices.length} categories. Review them, remove anything that doesn't belong, or add what we missed.`
            : 'Add the services your business offers. You can configure pricing and duration later.'}
        </p>
      </div>

      {/* Add service input */}
      <div className="flex gap-2">
        <Input
          placeholder="Enter service name..."
          value={newServiceName}
          onChange={(e) => setNewServiceName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustomService();
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={addCustomService}
          disabled={!newServiceName.trim()}
        >
          <PlusIcon className="h-4 w-4" />
          Add
        </Button>
      </div>

      {/* Services list */}
      {services.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-xl border bg-muted/40 px-4 py-2.5 text-xs text-muted-foreground">
            <span>{services.length} services</span>
            <span>{groupedServices.length} categories</span>
          </div>
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <ScrollArea
              aria-label="Categorized services"
              className="h-[min(34dvh,19rem)] sm:h-[min(50dvh,30rem)]"
              type="always"
            >
              <Accordion collapsible type="single">
                {groupedServices.map((group) => (
                  <AccordionItem
                    className="border-b last:border-b-0"
                    key={group.categoryName}
                    value={group.categoryName}
                  >
                    <AccordionTrigger className="rounded-none border-l-4 border-l-primary bg-primary/10 px-3 py-3 text-sm font-semibold tracking-tight text-foreground hover:bg-primary/15 hover:no-underline data-[state=open]:bg-primary/15 sm:px-4 sm:text-base [&>svg]:text-primary">
                      <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                        <span className="truncate">{group.categoryName}</span>
                        <span className="shrink-0 rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold tabular-nums text-primary-foreground">
                          {group.services.length}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="p-0">
                      <ul className="divide-y">
                        {group.services.map((service) => (
                          <li
                            key={service.name}
                            className="flex min-h-12 items-center justify-between gap-2 px-3 py-2.5"
                          >
                            {editingServiceName === service.name ? (
                              <Input
                                aria-label={`Edit ${service.name}`}
                                autoFocus
                                className="h-9 min-w-0"
                                value={editedServiceName}
                                onChange={(event) =>
                                  setEditedServiceName(event.target.value)
                                }
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    saveEditedService();
                                  }
                                  if (event.key === 'Escape') {
                                    cancelEditingService();
                                  }
                                }}
                              />
                            ) : (
                              <div className="min-w-0 space-y-1">
                                <p className="text-sm font-medium leading-snug">
                                  {service.name}
                                </p>
                                {details[service.name]?.description && (
                                  <p className="whitespace-pre-line text-sm text-muted-foreground">
                                    {details[service.name].description}
                                  </p>
                                )}
                              </div>
                            )}
                            <div className="flex shrink-0 items-center gap-0.5">
                              {editingServiceName === service.name ? (
                                <>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="size-8 text-muted-foreground hover:bg-primary/10 hover:text-primary sm:size-9"
                                    onClick={saveEditedService}
                                    disabled={
                                      !editedServiceName.trim() ||
                                      (editedServiceName.trim() !==
                                        service.name &&
                                        services.includes(
                                          editedServiceName.trim()
                                        ))
                                    }
                                  >
                                    <CheckIcon className="size-4" />
                                    <span className="sr-only">
                                      Save {service.name}
                                    </span>
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="size-8 text-muted-foreground sm:size-9"
                                    onClick={cancelEditingService}
                                  >
                                    <XIcon className="size-4" />
                                    <span className="sr-only">
                                      Cancel editing {service.name}
                                    </span>
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="size-8 text-muted-foreground hover:bg-primary/10 hover:text-primary sm:size-9"
                                  onClick={() =>
                                    startEditingService(service.name)
                                  }
                                >
                                  <PencilIcon className="size-4" />
                                  <span className="sr-only">
                                    Edit {service.name}
                                  </span>
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive sm:size-9"
                                onClick={() =>
                                  removeServiceFromForm(service.name)
                                }
                              >
                                <Trash2Icon className="size-4" />
                                <span className="sr-only">
                                  Remove {service.name}
                                </span>
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </ScrollArea>
          </div>
        </div>
      ) : (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ScissorsIcon />
            </EmptyMedia>
            <EmptyTitle>No services yet</EmptyTitle>
            <EmptyDescription>
              Type a service name above and click Add to get started.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
    </FieldGroup>
  );
}
